import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQuestions, clearQuestions } from '../../store/slices/questionSlice';
import { fetchAnswers } from '../../store/slices/answerSlice';
import { analyzeProfile, sendProfileResults } from '../../store/slices/profilingSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { flowLogger } from '../../utils/flowLogger';

// Import de l'image
import homepageImage from '../../assets/images/illustrations/homepage.png';

const QuestionStep = ({
  kid,
  currentStep,
  totalSteps,
  onNext,
  onAnswer
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { questions, loading: questionsLoading, error: questionError, loadedForAgeSectionId } = useSelector((state) => state.question);
  const { answers, loading: answersLoading } = useSelector((state) => state.answer);
  const { loading: profilingLoading, error: profilingError } = useSelector((state) => state.profiling);

  const [selectedOption, setSelectedOption] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const ageSectionId = kid?.ages_section_id != null ? String(kid.ages_section_id) : null;
  const isSyncedWithAge = ageSectionId && loadedForAgeSectionId === ageSectionId;
  const effectiveTotalSteps = isSyncedWithAge ? questions.length : 0;

  const loadQuestionsForAge = () => {
    if (!ageSectionId) return;
    flowLogger('profiling.questions.fetch', { ageSectionId });
    dispatch(fetchQuestions(ageSectionId));
  };

  // Récupérer les questions et réponses quand la tranche d'âge change
  useEffect(() => {
    if (!ageSectionId) {
      flowLogger('profiling.questions.skip', { reason: 'no_age_section' });
      return;
    }

    if (loadedForAgeSectionId !== ageSectionId) {
      setCurrentQuestionIndex(0);
      setSelectedOption(null);
      setUserAnswers([]);
      loadQuestionsForAge();
    }

    if (!answers || answers.length === 0) {
      dispatch(fetchAnswers());
    }
  }, [dispatch, ageSectionId, loadedForAgeSectionId]);

  useEffect(() => {
    if (!questionsLoading && ageSectionId && loadedForAgeSectionId === ageSectionId) {
      if (questions.length === 0) {
        flowLogger('profiling.questions.empty', { ageSectionId });
      }
    }
  }, [questionsLoading, questions.length, ageSectionId, loadedForAgeSectionId]);

  useEffect(() => {
    if (questionError) {
      flowLogger('profiling.questions.error', { ageSectionId, error: questionError });
    }
  }, [questionError, ageSectionId]);

  // Récupérer la question actuelle
  const currentQuestion = questions[currentQuestionIndex];
  const questionAnswers = answers.filter(answer =>
    answer.question_id === currentQuestion?.id
  );

  const handleSelectOption = (optionId) => {
    setSelectedOption(optionId);
    onAnswer(currentQuestion.id, optionId);

    // Ajouter la réponse à la liste des réponses utilisateur
    const newAnswer = {
      question_id: currentQuestion.id,
      answer_id: optionId
    };
    setUserAnswers([...userAnswers, newAnswer]);
  };

  const handleNext = async () => {
    if (selectedOption === null) return;

    // Si c'est la dernière question, procéder à l'analyse
    if (currentQuestionIndex === effectiveTotalSteps - 1) {
      await handleAnalyzeProfile();
    } else {
      // Passer à la question suivante
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
      onNext();
    }
  };

  const handleAnalyzeProfile = async () => {
    setIsAnalyzing(true);

    try {
      // Préparer les données pour l'analyse
      const answerIds = userAnswers.map(answer => answer.answer_id);

      // Analyser le profil
      const analysisResult = await dispatch(analyzeProfile({
        kidId: kid.id,
        answers: answerIds,
        ageSectionId: kid.ages_section_id
      })).unwrap();

      console.log('Résultat de l\'analyse:', analysisResult);

      // Afficher les résultats à l'utilisateur
      // Le backend renvoie les données dans un sous-objet 'profile'
      const profileData = analysisResult.profile || analysisResult;
      
      console.log('Données de profil à afficher:', profileData);
      
      toast.success(
        <div>
          <p>🎉 <strong>Profil déterminé pour {kid.firstname} !</strong></p>
          <p>📊 Profil: {profileData.profil_type_name || 'Non déterminé'}</p>
          <p>📝 <b>WoW, 🤩! </b> {profileData.profil_type_description || 'Non disponible'}</p>
          <p>🎯 Confiance: {profileData.confidence ? Math.round(profileData.confidence * 100) : 0}%</p>
          <p>📧 Un email avec les détails a été envoyé aux parents!</p>
        </div>, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
        }
      );

      // Envoyer les résultats par email
      try {
        await dispatch(sendProfileResults({
          kidId: kid.id,
        })).unwrap();

        console.log('Email envoyé avec succès');
        
        // Rediriger vers la page de remerciement
        setTimeout(() => {
          navigate('/Thanks');
        }, 1000); // Délai court pour que l'utilisateur puisse voir l'alerte
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi des résultats par email:', emailError);
        toast.warn(`  Profil analysé avec succès, mais erreur d'envoi d'email: ${emailError.message}`, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
        });
        
        // Rediriger quand même vers la page de remerciement malgré l'erreur d'email
        setTimeout(() => {
          navigate('/Thanks');
        }, 1500);
      }

    } catch (error) {
      console.error('Erreur lors de l\'analyse du profil:', error);
      toast.error(`  Erreur: ${error.message || 'Impossible de terminer l\'analyse'}`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const renderShell = (content) => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#FFF9F0',
          px: 2,
          gap: 2,
        }}
      >
        {content}
      </Box>
    </>
  );

  if (!kid) {
    flowLogger('profiling.questions.skip', { reason: 'no_kid' });
    return renderShell(
      <>
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}>
          Profil enfant introuvable. Rechargez la page ou recommencez depuis l&apos;accueil.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/HomePage')} sx={{ backgroundColor: '#784e3c' }}>
          Retour à l&apos;accueil
        </Button>
      </>
    );
  }

  if (!ageSectionId) {
    return renderShell(
      <>
        <Alert severity="warning" sx={{ maxWidth: 480, width: '100%' }}>
          La tranche d&apos;âge n&apos;est pas encore enregistrée pour {kid.firstname || 'cet enfant'}.
        </Alert>
        <Typography textAlign="center" color="#666">
          Retournez à l&apos;étape précédente pour choisir une tranche d&apos;âge.
        </Typography>
      </>
    );
  }

  // Entre invalidation cache et fetch : éviter l'écran beige sans contenu interactif
  if (!isSyncedWithAge || questionsLoading) {
    return renderShell(
      <>
        <CircularProgress size={60} sx={{ color: '#784e3c' }} />
        <Typography sx={{ color: '#2C3E50', fontWeight: 600, textAlign: 'center' }}>
          Chargement du questionnaire...
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
          Préparation des questions pour la tranche d&apos;âge sélectionnée.
        </Typography>
      </>
    );
  }

  if (!currentQuestion && questions.length === 0) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#FFF9F0',
            px: 2,
            gap: 2,
          }}
        >
          {questionError && (
            <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}>
              {questionError}
            </Alert>
          )}
          <Typography textAlign="center">
            Aucune question disponible pour cette tranche d&apos;âge.
            {questionError
              ? ' Une erreur est survenue lors du chargement.'
              : ' Vérifiez que des questions existent dans le backoffice pour cette tranche.'}
          </Typography>
          <Button
            variant="contained"
            onClick={loadQuestionsForAge}
            disabled={questionsLoading || !ageSectionId}
            sx={{
              backgroundColor: '#784e3c',
              '&:hover': { backgroundColor: '#B58136' },
            }}
          >
            Réessayer
          </Button>
        </Box>
      </>
    );
  }

  if (!currentQuestion) {
    return renderShell(
      <>
        {questionError && (
          <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}>
            {questionError}
          </Alert>
        )}
        <Typography textAlign="center" sx={{ color: '#2C3E50', fontWeight: 600 }}>
          Erreur : question non trouvée. Le questionnaire a peut-être changé.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            dispatch(clearQuestions());
            loadQuestionsForAge();
          }}
          sx={{ backgroundColor: '#784e3c', '&:hover': { backgroundColor: '#B58136' } }}
        >
          Recharger le questionnaire
        </Button>
      </>
    );
  }

  if (effectiveTotalSteps === 0) {
    return renderShell(
      <>
        <Alert severity="warning" sx={{ maxWidth: 480, width: '100%' }}>
          Aucune question n&apos;est disponible pour cette tranche d&apos;âge.
        </Alert>
        <Button
          variant="contained"
          onClick={loadQuestionsForAge}
          sx={{ backgroundColor: '#784e3c', '&:hover': { backgroundColor: '#B58136' } }}
        >
          Réessayer
        </Button>
      </>
    );
  }

  if (!answersLoading && questionAnswers.length === 0) {
    flowLogger('profiling.questions.error', { ageSectionId, error: 'no_answers_for_question', questionId: currentQuestion.id });
    return renderShell(
      <>
        <Alert severity="error" sx={{ maxWidth: 520, width: '100%' }}>
          Les réponses de cette question n&apos;ont pas pu être chargées.
        </Alert>
        <Typography textAlign="center" sx={{ color: '#666' }}>
          Question : {currentQuestion.content}
        </Typography>
        <Button
          variant="contained"
          onClick={() => dispatch(fetchAnswers())}
          sx={{ backgroundColor: '#784e3c', '&:hover': { backgroundColor: '#B58136' } }}
        >
          Recharger les réponses
        </Button>
      </>
    );
  }

  console.log('Affichage question:', currentQuestion);
  console.log('Réponses pour cette question:', questionAnswers);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#FFF9F0',
          px: 3,
          pt: 6
        }}
    >
      {/* Stepper - Indicateurs d'étapes avec lignes de connexion */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 6,
          px: 2
        }}
      >
        {Array.from({ length: effectiveTotalSteps }).map((_, index) => (
          <React.Fragment key={index}>
            {/* Cercle d'étape */}
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                border: index <= currentStep ? '1px solid #784e3c' : '1px dashed #D0D0D0',
                backgroundColor: index < currentStep ? 'white' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 2
              }}
            >
              {index < currentStep && (
                <CheckCircleIcon sx={{ color: '#784e3c ', fontSize: 24 }} />
              )}
            </Box>

            {/* Ligne de connexion (sauf pour le dernier élément) */}
            {index < effectiveTotalSteps - 1 && (
              <Box
                sx={{
                  width: '10px',
                  height: '3px',
                  backgroundColor: index < currentStep ? '#784e3c' : '#D0D0D0',
                  transition: 'background-color 0.3s ease'
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Image de l'enfant */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <img
          src={homepageImage}
          alt="Enfant joyeux"
          style={{
            width: '180px',
            height: '180px',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Question actuelle */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 'bold',
          color: '#2C3E50',
          mb: 4,
          textAlign: 'center',
          fontSize: { xs: '1.3rem', sm: '1.6rem' },
          px: 2,
          fontFamily: 'Achiko'
        }}
      >
        {currentQuestion.content}
      </Typography>

      {/* Affichage des erreurs de profilage */}
      {profilingError && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>
          {profilingError}
        </Alert>
      )}

      {/* Options de réponse */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 4
        }}
      >
        {answersLoading ? (
          <>
            <CircularProgress size={32} sx={{ alignSelf: 'center', color: '#784e3c' }} />
            <Typography textAlign="center" color="#666">
              Chargement des réponses...
            </Typography>
          </>
        ) : (
          questionAnswers.map((answer) => (
          <Box
            key={answer.id}
            onClick={() => handleSelectOption(answer.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2.5,
              backgroundColor: 'white',
              border: selectedOption === answer.id ? '3px solid #784e3c' : '3px solid transparent',
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: selectedOption === answer.id
                ? '0px 4px 15px rgba(181, 129, 54, 0.3)'
                : '0px 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow: '0px 4px 15px rgba(181, 129, 54, 0.2)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: selectedOption === answer.id ? 'bold' : 'normal',
                color: '#2C3E50',
                fontSize: { xs: '1rem', sm: '1.2rem' }
              }}
            >
              {answer.content}
            </Typography>
          </Box>
        ))
        )}
      </Box>

      {/* Bouton Suivant / Analyser */}
      <Button
        onClick={handleNext}
        disabled={selectedOption === null || isAnalyzing || answersLoading}
        variant="contained"
        fullWidth
        sx={{
          maxWidth: '600px',
          backgroundColor: currentQuestionIndex === effectiveTotalSteps - 1 ? '#784e3c' : '#784e3c',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          textTransform: 'none',
          borderRadius: '50px',
          py: 1.5,
          boxShadow: '0px 4px 10px rgba(181, 129, 54, 0.4)',
          '&:hover': {
            backgroundColor: currentQuestionIndex === effectiveTotalSteps - 1 ? '#755A35' : '#B58136',
            boxShadow: '0px 6px 15px rgba(181, 129, 54, 0.6)',
          },
          '&:disabled': {
            backgroundColor: '#D0D0D0',
            color: '#888888'
          }
        }}
      >
        {isAnalyzing || profilingLoading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Analyse en cours...
          </>
        ) : currentQuestionIndex === effectiveTotalSteps - 1 ? (
          'Analyser et terminer'
        ) : (
          'Suivant'
        )}
      </Button>

      {/* Informations sur la progression */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: '#666',
          mt: 2
        }}
      >
        Question {currentQuestionIndex + 1} sur {effectiveTotalSteps}
      </Typography>
    </Box>
    </>
  );
};

export default QuestionStep;